import React from 'react';

import { useAppSelector } from 'utils/hooks';
import { renderModal } from 'utils/modal_util';

import ui from 'domains/ui';

import Button from 'components/shared/Button';

import type { MouseEventHandler, TouchEventHandler } from 'react';

let menuDelay: number | undefined;
const menuDelayTimer = 200;

type FlyoutProps = {
  children: React.ReactNode;
  flyoutOpen: boolean;
  heading: string;
  setFlyoutOpen: (set: boolean) => void;
  subheading: string | null;
  description: string | null;
  leaveFlyoutOpen?: boolean;
};
type FlyoutHeaderProps = Pick<
  FlyoutProps,
  'heading' | 'subheading' | 'description' | 'setFlyoutOpen' | 'leaveFlyoutOpen'
>;
type FlyoutMenuProps = Pick<FlyoutProps, 'children' | 'setFlyoutOpen' | 'leaveFlyoutOpen'>;

type FlyoutHeaderAttrs = {
  onTouchEnd?: TouchEventHandler;
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
};

export default function Flyout(props: FlyoutProps) {
  const { children, flyoutOpen, ...rest } = props;

  return (
    <React.Fragment>
      <FlyoutHeader {...rest} />
      {flyoutOpen && renderModal(<FlyoutMenu {...rest}>{children}</FlyoutMenu>)}
    </React.Fragment>
  );
}

function FlyoutHeader(props: FlyoutHeaderProps) {
  const { heading, subheading, description, setFlyoutOpen, leaveFlyoutOpen } = props;
  const hasMobileNavigation = useAppSelector(ui.selectors.hasMobileNavigation);

  function handleIconClick() {
    if (hasMobileNavigation) {
      setFlyoutOpen(true);
    }
  }

  const atts: FlyoutHeaderAttrs = !hasMobileNavigation
    ? {
        onTouchEnd: (e) => {
          window.clearTimeout(menuDelay);
          setFlyoutOpen(true);
          e.stopPropagation();
          e.preventDefault();
        },
        onMouseEnter: () => {
          window.clearTimeout(menuDelay);
          setFlyoutOpen(true);
        },
        onMouseLeave: () => {
          if (!leaveFlyoutOpen) {
            menuDelay = window.setTimeout(() => {
              setFlyoutOpen(false);
            }, menuDelayTimer);
          }
        },
      }
    : {};

  return (
    <div className="flyout_header" {...atts}>
      <div className="sidebar-section sidebar_header_wrapper">
        <header id="sidebar-header" className="sidebar-section__header sidebar_header">
          <h1 dangerouslySetInnerHTML={{ __html: heading }} />
          {subheading && <p>{subheading}</p>}
          {description && <p>{description}</p>}
        </header>

        <Button
          className="flyout_icon"
          type="icon"
          icon="angle-right"
          size="2x"
          appearance="light"
          onClick={handleIconClick}
        />
      </div>
    </div>
  );
}

function FlyoutMenu(props: FlyoutMenuProps) {
  const { children, setFlyoutOpen, leaveFlyoutOpen } = props;
  const hasMobileNavigation = useAppSelector(ui.selectors.hasMobileNavigation);

  const atts = !hasMobileNavigation
    ? {
        onMouseEnter: () => {
          window.clearTimeout(menuDelay);
          setFlyoutOpen(true);
        },
        onMouseLeave: () => {
          if (!leaveFlyoutOpen) {
            menuDelay = window.setTimeout(() => {
              setFlyoutOpen(false);
            }, menuDelayTimer);
          }
        },
      }
    : {};

  return (
    <div className="sidebar-flyoutmenu" {...atts}>
      <div className="FlyoutMenu__header">
        <Button
          type="icon"
          icon="times"
          onClick={() => setFlyoutOpen(false)}
          appearance="light"
          a11yText="Close subcatalog menu"
          size="lg"
        />
      </div>
      {children}
    </div>
  );
}
